import { v4 as uuid } from '@lukeed/uuid'

import { convertStepAttackToScenarioGraphAndSteps } from 'components/attacks/utils/attacks'
import { type ScenarioLocationState } from 'components/scenarios/create/create-edit-utils'
import theme from 'theme'
import { type TrafficImpactMapping } from 'types/api/attacks/details/create'
import {
  TargetType,
  type InfraAttack
} from 'types/api/attacks/details/infra-attack'
import {
  type AttackCliArgs,
  type AttackConfiguration
} from 'types/api/attacks/details/targeting'
import { RequestSource } from 'types/api/gamedays/gamedays'
import { type K8sAttack, type K8sSubAttack } from 'types/api/kubernetes/attacks'
import { type StepAttack } from 'types/api/scenarios/scenario'
import { keysToSnakeCase } from 'utils/obj'

const DOT_COLOR = {
  SUCCESSFUL: theme.colors.green[0],
  RESOLVING: theme.colors.blue[0],
  LAUNCHED: theme.colors.blue[0],
  // UserHalted: theme.colors.steel[1], Halting is handled by the ILFI stage utils
  ERROR: theme.colors.red[0]
}

export function isKubernetesStage(stage) {
  return Object.keys(DOT_COLOR).includes(stage)
}

export function getK8AttackStageColor(stage) {
  return DOT_COLOR[stage]
}

export function isKubernetesAttackActive(k8Attack) {
  return k8Attack.stage === 'RESOLVING'
}

/**
 * Detect if an attack originates from k8
 *
 * Basic shape of a k8 attack
 * {
    "uid": "cc7202eb-1b80-48c2-b202-eb1b8008c27c",
    "attack": {},
    "state": "UNRESOLVED",
    "createdAt": "2019-10-31T22:02:25.811Z",
    "updatedAt": "2019-10-31T22:02:25.811Z"
  }
 */
export function isKubernetesAttack(
  attack: K8sAttack | InfraAttack
): attack is K8sAttack {
  return Boolean('uid' in attack && 'attack' in attack)
}

export type FormattedIlfiAttack = Partial<
  Omit<InfraAttack, 'attack_configuration' | 'traffic_impact_mapping'>
> & {
  attack: K8sSubAttack
  attackConfiguration: AttackConfiguration
  trafficImpactMapping: TrafficImpactMapping
  uid: string
}

export function formatK8AttackIntoIlfiAttack({
  attackId: _attackid,
  scenarioNodeGuid: _scenarioNodeGuid,
  attackConfiguration,
  ...k8Attack
}: K8sAttack): FormattedIlfiAttack {
  const cliArgs =
    k8Attack.attack.impactDefinition?.cliArgs ??
    ([] as unknown as AttackCliArgs)
  const containerSelection =
    k8Attack.attack.targetDefinition?.strategy?.containerSelection
  const snakeCasedK8Attack = keysToSnakeCase(k8Attack)

  return {
    ...snakeCasedK8Attack,
    args: cliArgs,
    attackConfiguration,
    containerSelection,
    guid: k8Attack.uid,
    infra_command: {
      commandType: cliArgs[0],
      commandArgs: cliArgs
    },
    kind: RequestSource.WebApp,
    providers: k8Attack.attack.impactDefinition?.providers ?? [],
    start_time: k8Attack.createdAt,
    target_type: TargetType.Kubernetes,
    targets: (k8Attack.attack.targetDefinition?.strategy?.k8sObjects ?? []).map(
      (t) => t.name || t.uid
    ),
    trafficImpactMapping: k8Attack.attack.impactDefinition?.trafficImpactMapping
  }
}

export function convertK8sAttackToScenarioGraph(
  reusedAttack: K8sAttack,
  name: string,
  description: string,
  hypothesis: string
): ScenarioLocationState {
  const step: StepAttack = {
    attackConfiguration: reusedAttack.attackConfiguration,
    impactDefinition: {
      commandArgs: {
        cliArgs: reusedAttack?.args ?? [],
        providers: reusedAttack.providers ?? [],
        trafficImpactMapping: reusedAttack.trafficImpactMapping
      },
      commandType: reusedAttack?.infra_command?.commandType ?? ''
    },
    targetDefinition: {
      targetType:
        reusedAttack?.attack?.targetDefinition?.targetType ?? 'Kubernetes',
      strategy: reusedAttack?.attack?.targetDefinition?.strategy ?? {
        percentage: 100
      },
      strategyType:
        reusedAttack?.attack?.targetDefinition?.strategyType ?? 'Random'
    }
  }

  return {
    scenario: {
      description,
      guid: uuid(),
      hypothesis,
      name,
      ...convertStepAttackToScenarioGraphAndSteps(step)
    }
  }
}
